import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { printStandaloneHtmlDocument } from './modalDocumentExport';
import { WEEKLY_LEARNING_GUIDE_A4_CAPTURE_CSS } from './weeklyLearningGuideA4CaptureCss';
import { bakeWeeklyGuideHatchBackgroundsForHtml2Canvas } from './weeklyGuideHatchHtml2CanvasBake';
import { injectStylesIntoCloneDocument } from './documentExportLightTheme';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function mmToPx(mm: number): number {
  return (mm * 96) / 25.4;
}

/** 인쇄·PDF 한 장에 맞추기 위한 축소 비율(1 이하) */
export function measureWeeklyGuideA4PrintScale(root: HTMLElement): number {
  const a4W = mmToPx(A4_WIDTH_MM);
  const a4H = mmToPx(A4_HEIGHT_MM);
  const rect = root.getBoundingClientRect();
  const w = rect.width || root.offsetWidth || root.scrollWidth;
  const h = rect.height || root.offsetHeight || root.scrollHeight;
  if (w <= 0 || h <= 0) return 1;
  return Math.min(1, a4W / w, a4H / h);
}

const WEEKLY_GUIDE_A4_PRINT_SHELL_CSS = `
  @page {
    size: ${A4_WIDTH_MM}mm ${A4_HEIGHT_MM}mm;
    margin: 0;
  }
  html,
  body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body {
    display: block;
  }
  .cp-weekly-learning-guide-a4-term-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
  }
  .wlg-a4-print-port.cp-weekly-learning-guide-a4-sheet--page-break,
  .cp-weekly-learning-guide-a4-sheet--page-break {
    page-break-after: always;
    break-after: page;
  }
  .wlg-a4-print-port {
    width: ${A4_WIDTH_MM}mm;
    margin: 0 auto;
    overflow: visible;
    box-sizing: border-box;
  }
  .wlg-a4-print-port--single {
    height: ${A4_HEIGHT_MM}mm;
    overflow: hidden;
    page-break-after: avoid;
    break-inside: avoid;
  }
  .wlg-a4-print-scaled {
    transform-origin: top center;
  }
  .cp-weekly-learning-guide-a4-sheet {
    width: ${A4_WIDTH_MM}mm !important;
    height: ${A4_HEIGHT_MM}mm !important;
    min-height: ${A4_HEIGHT_MM}mm !important;
    max-height: ${A4_HEIGHT_MM}mm !important;
    box-shadow: none !important;
    margin: 0 !important;
    overflow: hidden !important;
    page-break-inside: avoid;
    break-inside: avoid;
    display: flex !important;
    flex-direction: column !important;
    box-sizing: border-box !important;
  }
  .cp-weekly-learning-guide-a4-sheet--neis-grid {
    padding: 6mm !important;
  }
  .cp-weekly-learning-guide-a4-neis-grid {
    display: flex !important;
    flex-direction: row !important;
    align-items: stretch !important;
    flex: 1 1 auto !important;
    min-height: 0 !important;
    width: 100% !important;
  }
  .cp-weekly-learning-guide-a4-neis-grid-col {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-start !important;
    align-items: stretch !important;
    gap: 4mm !important;
  }
  .cp-weekly-learning-guide-a4-neis-grid-column-divider {
    flex: 0 0 7mm !important;
    width: 7mm !important;
    align-self: stretch !important;
    background-image: repeating-linear-gradient(
      to bottom,
      #8b8d96 0,
      #8b8d96 5px,
      transparent 5px,
      transparent 10px
    ) !important;
    background-position: center center !important;
    background-size: 1.5px 10px !important;
    background-repeat: repeat-y !important;
    pointer-events: none !important;
  }
  .cp-weekly-learning-guide-a4-neis-grid-cell {
    display: flex !important;
    flex-direction: column !important;
    flex: 0 0 auto !important;
    min-width: 0 !important;
    overflow: hidden !important;
    padding-bottom: 2mm !important;
    box-sizing: border-box !important;
  }
  .cp-weekly-learning-guide-a4-neis-grid-col > .cp-weekly-learning-guide-a4-neis-grid-cell:last-child {
    padding-bottom: 0 !important;
  }
  .cp-weekly-learning-guide-a4-table-wrap--grid {
    flex: 0 0 auto !important;
  }
  .cp-weekly-learning-guide-a4-table-wrap--grid .cp-weekly-learning-guide-a4-table {
    height: auto !important;
  }
  .cp-weekly-learning-guide-a4-table,
  .cp-weekly-learning-guide-a4-table-wrap {
    page-break-inside: avoid;
    break-inside: avoid;
  }
`;

function isNeisGridSheet(el: HTMLElement): boolean {
  return el.classList.contains('cp-weekly-learning-guide-a4-sheet--neis-grid');
}

function buildWeeklyLearningGuideA4PrintHtml(root: HTMLElement, title: string, scale: number): string {
  const sheets = Array.from(root.querySelectorAll('.cp-weekly-learning-guide-a4-sheet')) as HTMLElement[];
  const targets = sheets.length > 0 ? sheets : [root];
  const isMultiPage = targets.length > 1;

  const pagesHtml = targets
    .map((sheet, index) => {
      const clone = sheet.cloneNode(true) as HTMLElement;
      const inlineStyle = sheet.getAttribute('style');
      if (inlineStyle) {
        clone.setAttribute('style', inlineStyle);
      }
      const sheetScale = isMultiPage || isNeisGridSheet(sheet) ? 1 : scale;
      const scaledInner =
        sheetScale < 0.999
          ? `<div class="wlg-a4-print-scaled" style="transform:scale(${sheetScale});">${clone.outerHTML}</div>`
          : clone.outerHTML;
      const portClass = isMultiPage ? 'wlg-a4-print-port' : 'wlg-a4-print-port wlg-a4-print-port--single';
      const pageBreak =
        isMultiPage && index < targets.length - 1
          ? ' cp-weekly-learning-guide-a4-sheet--page-break'
          : '';
      return `<div class="${portClass}${pageBreak}">${scaledInner}</div>`;
    })
    .join('\n');

  return `<!DOCTYPE html>
<html lang="ko">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      ${WEEKLY_LEARNING_GUIDE_A4_CAPTURE_CSS}
      ${WEEKLY_GUIDE_A4_PRINT_SHELL_CSS}
    </style>
  </head>
  <body>
    ${pagesHtml}
  </body>
</html>`;
}

/** 화면 미리보기와 동일한 DOM·CSS를 격리 iframe에서 인쇄 */
export function printWeeklyLearningGuideA4DocumentFromRoot(root: HTMLElement, title: string): void {
  const sheets = Array.from(root.querySelectorAll('.cp-weekly-learning-guide-a4-sheet')) as HTMLElement[];
  const hasNeisGridPage = sheets.some(isNeisGridSheet);
  const scale = sheets.length > 1 || hasNeisGridPage ? 1 : measureWeeklyGuideA4PrintScale(root);
  printStandaloneHtmlDocument(buildWeeklyLearningGuideA4PrintHtml(root, title, scale));
}

async function renderSheetToPdfPage(
  pdf: jsPDF,
  sheet: HTMLElement,
  pageIndex: number
): Promise<void> {
  const canvas = await html2canvas(sheet, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    onclone: (clonedDoc, clonedElement) => {
      injectStylesIntoCloneDocument(clonedDoc, WEEKLY_LEARNING_GUIDE_A4_CAPTURE_CSS);
      // inset box-shadow는 html2canvas에서 셀 배경 채움으로 잘못 렌더됨 → 제거
      clonedElement
        .querySelectorAll<HTMLElement>('.cp-weekly-learning-guide-cell--neis-short-increase')
        .forEach((el) => {
          el.style.boxShadow = 'none';
        });
      // repeating-linear-gradient 빗금은 html2canvas가 생략 → SVG 패턴으로 치환
      bakeWeeklyGuideHatchBackgroundsForHtml2Canvas(clonedElement);
    },
  });
  if (canvas.width < 2 || canvas.height < 2) {
    throw new Error('PDF 캡처에 실패했습니다.');
  }

  if (pageIndex > 0) {
    pdf.addPage();
  }

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgData = canvas.toDataURL('image/png');

  let drawW = pageW;
  let drawH = (canvas.height * drawW) / canvas.width;
  if (drawH > pageH) {
    drawH = pageH;
    drawW = (canvas.width * drawH) / canvas.height;
  }
  const x = (pageW - drawW) / 2;
  const y = (pageH - drawH) / 2;
  pdf.addImage(imgData, 'PNG', x, y, drawW, drawH);
}

/** 화면 미리보기와 동일한 전체 시트를 A4 PDF로 저장(주별 다중 페이지 지원) */
export async function saveWeeklyLearningGuideA4DocumentAsPdf(params: {
  root: HTMLElement;
  fileName: string;
}): Promise<void> {
  const { root, fileName } = params;
  const sheets = Array.from(root.querySelectorAll('.cp-weekly-learning-guide-a4-sheet')) as HTMLElement[];

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  if (sheets.length > 0) {
    for (let i = 0; i < sheets.length; i += 1) {
      await renderSheetToPdfPage(pdf, sheets[i]!, i);
    }
    pdf.save(fileName);
    return;
  }

  const scale = measureWeeklyGuideA4PrintScale(root);
  const canvas = await html2canvas(root, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    onclone: (clonedDoc, clonedElement) => {
      injectStylesIntoCloneDocument(clonedDoc, WEEKLY_LEARNING_GUIDE_A4_CAPTURE_CSS);
      clonedElement
        .querySelectorAll<HTMLElement>('.cp-weekly-learning-guide-cell--neis-short-increase')
        .forEach((el) => {
          el.style.boxShadow = 'none';
        });
      bakeWeeklyGuideHatchBackgroundsForHtml2Canvas(clonedElement);
    },
  });
  if (canvas.width < 2 || canvas.height < 2) {
    throw new Error('PDF 캡처에 실패했습니다.');
  }

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgData = canvas.toDataURL('image/png');

  let drawW = pageW * scale;
  let drawH = (canvas.height * drawW) / canvas.width;
  if (drawH > pageH) {
    drawH = pageH;
    drawW = (canvas.width * drawH) / canvas.height;
  }
  const x = (pageW - drawW) / 2;
  const y = (pageH - drawH) / 2;
  pdf.addImage(imgData, 'PNG', x, y, drawW, drawH);
  pdf.save(fileName);
}

/** PDF 저장 후 탭 닫기(스크립트로 연 탭에서만 동작) */
export async function saveWeeklyLearningGuideA4DocumentAsPdfAndClose(params: {
  root: HTMLElement;
  fileName: string;
}): Promise<void> {
  await saveWeeklyLearningGuideA4DocumentAsPdf(params);
  window.setTimeout(() => {
    window.close();
  }, 300);
}
